/**
 * Ligação do `FeedClient` aos sinais (AC-2.4-01; §2.2.3 passos 3–5, §14.8, §15.7.4; FE §6.6, §6.8, DE-B06, DE-21).
 *
 * Único escritor de `feed`, `liveView` e `historySeries` (os três num único `batch()` por atualização do cliente) e
 * dos estáticos que chegam depois do boot (`statics` e `ctx`; o cadastro do boot é do main.tsx). Também:
 * - registra o cliente na porta das ações (`attachFeedClient`, DE-21) e liga a página a ele (aba oculta, rede,
 *   bfcache: `attachLifecycle`, §10.9);
 * - recalcula a conexão quando `timeline.live` muda (§10.6.1: `replay` é derivado);
 * - aplica a trava de sessão da recarga pedida por `ops.reload_build` num snapshot VÁLIDO (M9, §14.8.5);
 * - etapa 3 de §15.7.4: `geography`, `first-round` e `baseline-2022` 1,5 s depois do 1º número (ou, sem número, depois
 *   de `secondaryMaxWaitMs`); `geography` já na partida quando a rota inicial é de município, Exterior, país ou
 *   cidade (DE-B10, exceção);
 * - `recreate(query)` do DevPanel (DE-B13): para o cliente, cria fonte e cliente novos e repete a ligação.
 */
import { batch, effect } from '@preact/signals';
import type { AppConfig } from '../config';
import type { CandidateNumber, UfCode } from '../contracts/base';
import type { Geography } from '../contracts/static';
import { attachLifecycle, createFeedClient, type FeedClient, type FeedUpdate } from '../data/feedClient';
import type { DataSource, Statics } from '../data/source';
import type { ResultsView, RouteState } from '../model/types';
import { attachFeedClient } from '../state/actions';
import { resetDataClock } from '../state/effects';
import { claimReload } from '../state/storage';
import {
  commentaryPath,
  commentaryRev,
  commentaryStatus,
  ctx,
  feed,
  historySeries,
  insightsPath,
  liveView,
  probabilityDetail,
  route,
  statics,
  timeline,
} from '../state/store';
import { homeShown } from './sitePhase';

/**
 * B5 (pré-mortem, ORD-F-148): na página inicial (fase `home`, antes de 25/10), uma busca do snapshot a cada 5 min, e
 * não a cada 15–30 s (o feed ainda não existe: cada busca volta 404). Ao sair da página inicial, busca na hora.
 */
export const HOME_POLL_MS = 5 * 60_000;

/** Etapa 3 (§15.7.4): 1,5 s depois do 1º número. */
export const SECONDARY_AFTER_FIRST_MS = 1_500;
/** Sem número (feed ausente, "Conectando"), os estáticos de contexto saem assim mesmo depois deste tempo. */
export const SECONDARY_MAX_WAIT_MS = 3_000;

export interface FeedBindingOptions {
  source: DataSource;
  config: AppConfig;
  /** Estáticos da fonte (`source.statics()`, já resolvido no boot: o cadastro bloqueia o render, §2.2.4). */
  statics: Statics;
  /** Build de produção (I-17; `PRODUCTION_BUILD` de createSource.ts). */
  production: boolean;
  /** Grava o cadastro em `statics`/`ctx` (na recriação; no boot quem grava é o main.tsx). */
  writeCandidates?: boolean;
  doc?: Document;
  win?: Window;
  /** Recarga da página (padrão: `location.reload()`). */
  reload?: () => void;
  secondaryAfterFirstMs?: number;
  secondaryMaxWaitMs?: number;
}

export interface FeedBinding {
  readonly source: DataSource;
  readonly client: FeedClient;
  stop(): void;
}

/** A vista inicial precisa da geografia já na etapa 1 (DE-B10). */
function needsGeographyNow(r: RouteState): boolean {
  const k = r.place.kind;
  return k === 'municipality' || k === 'abroad' || k === 'country' || k === 'abroad_city';
}

/** Líder nacional (resolve `cand` ausente, §5.2.4): número do candidato com `leader`, ou null. */
function nationalLeaderOf(v: ResultsView): CandidateNumber | null {
  const i = v.national.leader;
  return i === null ? null : (v.candidates[i]?.number ?? null);
}

/** Cadastro → contexto da rota (§5.1): lista e UFs com 2º turno de governador. main.tsx e a recriação usam. */
export function candidatesContext(s: Pick<Statics, 'candidates'>) {
  return {
    candidates: s.candidates,
    governorUfs: new Set(Object.keys(s.candidates.governor) as UfCode[]) as ReadonlySet<UfCode>,
  };
}

/** Liga um cliente novo à fonte e aos sinais. */
export function bindFeed(o: FeedBindingOptions): FeedBinding {
  const doc = o.doc ?? document;
  const win = o.win ?? window;
  const cfg = o.config;
  let stopped = false;
  let geography: Geography | null = null;
  let firstNumber = false;
  const timers: ReturnType<typeof setTimeout>[] = [];
  const later = (fn: () => void, ms: number) => {
    timers.push(setTimeout(fn, ms));
  };

  if (o.writeCandidates) {
    batch(() => {
      statics.value = { candidates: o.statics.candidates, geography: null, firstRound: undefined, baseline: undefined };
      ctx.value = { ...ctx.peek(), ...candidatesContext(o.statics), geo: null, nationalLeader: null };
    });
  }

  const apply = (u: FeedUpdate) => {
    if (stopped) return;
    const v = u.liveView;
    batch(() => {
      feed.value = u.feed;
      if (v) liveView.value = v;
      if (u.historySeries) historySeries.value = u.historySeries;
      if (u.insightsPath !== undefined) insightsPath.value = u.insightsPath;
      if (u.commentaryPath !== undefined) commentaryPath.value = u.commentaryPath;
      if (u.commentaryRev !== undefined) commentaryRev.value = u.commentaryRev;
      if (u.probabilityDetail !== undefined) probabilityDetail.value = u.probabilityDetail;
      if (v) {
        const leader = nationalLeaderOf(v);
        if (ctx.peek().nationalLeader !== leader) ctx.value = { ...ctx.peek(), nationalLeader: leader };
      }
    });
    if (v && !firstNumber) {
      firstNumber = true;
      later(loadSecondary, o.secondaryAfterFirstMs ?? SECONDARY_AFTER_FIRST_MS);
    }
  };

  const client = createFeedClient({
    source: o.source,
    config: {
      mode: cfg.mode,
      build: cfg.build,
      poll_interval_s: cfg.poll_interval_s,
      election_date: cfg.election_date,
      round: cfg.round,
      features: { probability: cfg.features.probability, governors: cfg.features.governors },
    },
    candidates: o.statics.candidates,
    geography: () => geography,
    isProductionBuild: o.production,
    onUpdate: apply,
    onReloadRequested: (build, delayMs) => {
      // Uma vez por valor de build nesta sessão (sem sessionStorage, não recarrega: §2.4.4).
      if (stopped || !claimReload(build)) return;
      later(() => (o.reload ?? (() => win.location.reload()))(), delayMs);
    },
    timelineLive: () => timeline.peek().live,
    hidden: () => doc.visibilityState === 'hidden',
    minDelayMs: () => (cfg.features.site_phases && homeShown.peek() ? HOME_POLL_MS : 0),
  });

  // ── Etapa 3 e exceção da geografia ──
  let geographyAsked = false;
  const loadGeography = () => {
    if (geographyAsked || stopped) return;
    geographyAsked = true;
    o.statics.geography.then(
      (g) => {
        if (stopped) return;
        geography = g;
        batch(() => {
          statics.value = { ...statics.peek(), geography: g };
          ctx.value = { ...ctx.peek(), geo: g };
        });
        client.refreshView();
      },
      () => {
        // Sem geography.json (ou inválido): os blocos que dependem dele mostram o próprio estado (§13.3).
      },
    );
  };
  let secondaryAsked = false;
  function loadSecondary() {
    if (secondaryAsked || stopped) return;
    secondaryAsked = true;
    loadGeography();
    o.statics.firstRound.then(
      (firstRound) => {
        if (!stopped) statics.value = { ...statics.peek(), firstRound };
      },
      () => {
        if (!stopped) statics.value = { ...statics.peek(), firstRound: null };
      },
    );
    o.statics.baseline.then(
      (baseline) => {
        if (!stopped) statics.value = { ...statics.peek(), baseline };
      },
      () => {
        if (!stopped) statics.value = { ...statics.peek(), baseline: null };
      },
    );
  }
  if (needsGeographyNow(route.peek())) loadGeography();
  later(loadSecondary, o.secondaryMaxWaitMs ?? SECONDARY_MAX_WAIT_MS);

  // ── B5: a página inicial busca devagar; ao sair dela (00h00 de 25/10, ou a apuração aberta na fase `after`), na hora ──
  let prevHome = homeShown.peek();
  const disposePhase = effect(() => {
    const home = homeShown.value;
    const was = prevHome;
    prevHome = home;
    if (was && !home && cfg.features.site_phases) void client.refreshNow();
  });

  // ── Conexão derivada de timeline.live (§10.6.1) ──
  let firstRun = true;
  const disposeLive = effect(() => {
    void timeline.value.live;
    if (firstRun) {
      firstRun = false;
      return;
    }
    client.refreshConnection();
  });

  const detachLifecycle = attachLifecycle(client, doc, win);
  attachFeedClient(client);
  client.start();

  return {
    source: o.source,
    client,
    stop() {
      if (stopped) return;
      stopped = true;
      for (const t of timers.splice(0)) clearTimeout(t);
      disposeLive();
      disposePhase();
      detachLifecycle();
      client.stop();
      attachFeedClient(null);
      insightsPath.value = null;
      commentaryPath.value = null;
      commentaryStatus.value = null;
      probabilityDetail.value = null;
    },
  };
}

/** Fábrica da fonte (createSource) injetável: a recriação do DevPanel passa a query nova. */
export type SourceFactory = (config: AppConfig, query: URLSearchParams) => Promise<DataSource>;

export interface FeedRuntimeOptions {
  source: DataSource;
  statics: Statics;
  config: AppConfig;
  production: boolean;
  /** Para `recreate` (DevPanel): normalmente `createSource`. */
  createSource?: SourceFactory;
  doc?: Document;
  win?: Window;
}

/**
 * Ligação viva do app: a fonte atual (o relógio da `LiveRegion` e do tique de `now` segue a fonte nova), a partida antes do
 * 1º render (main.tsx; o App chama de novo, sem efeito) e a recriação pedida pelo DevPanel.
 */
export class FeedRuntime {
  private current: FeedBinding | null = null;
  private src: DataSource;
  private readonly listeners = new Set<(source: DataSource) => void>();
  private generation = 0;

  constructor(private readonly o: FeedRuntimeOptions) {
    this.src = o.source;
  }

  /** Fonte atual. */
  get source(): DataSource {
    return this.src;
  }

  /** Relógio da fonte atual (DE-B03). */
  readonly clock = (): number => this.src.clock();

  /** Liga o cliente (main.tsx, antes do 1º render; o App repete na montagem). Idempotente. */
  start(): void {
    if (this.current) return;
    this.current = bindFeed({
      source: this.src,
      config: this.o.config,
      statics: this.o.statics,
      production: this.o.production,
      doc: this.o.doc,
      win: this.o.win,
    });
  }

  stop(): void {
    this.generation++;
    this.current?.stop();
    this.current = null;
  }

  /** Avisa a troca de fonte (o App re-renderiza o DevPanel com a fonte nova). */
  subscribe(fn: (source: DataSource) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** `recriar()` do DevPanel (§14.9.9, DE-B13): para o cliente, cria fonte e cliente novos com a query nova. */
  async recreate(query: URLSearchParams): Promise<void> {
    const create = this.o.createSource;
    if (!create) return;
    const gen = ++this.generation;
    this.current?.stop();
    this.current = null;
    const source = await create(this.o.config, query);
    const st = await source.statics();
    if (gen !== this.generation) return;
    this.src = source;
    resetDataClock();
    this.current = bindFeed({
      source,
      config: this.o.config,
      statics: st,
      production: this.o.production,
      writeCandidates: true,
      doc: this.o.doc,
      win: this.o.win,
    });
    for (const fn of this.listeners) fn(source);
  }
}
